import { useClipboard } from "@dashboard/hooks/useClipboard";
import { Button, sprinkles } from "@saleor/macaw-ui-next";
import { Check, Copy } from "lucide-react";
import React from "react";
import { useIntl } from "react-intl";

import { formatAddressText, FormattableAddress } from "./formatAddress";
import { messages } from "./messages";

interface CopyAddressButtonProps {
  address: FormattableAddress;
  "data-test-id"?: string;
}

const ICON_SIZE = 16;

/**
 * Icon button that copies the address to the clipboard, laid out exactly as
 * `AddressFormatter` renders it. The icon flips to a check mark for two seconds
 * after copying, matching the copy affordances elsewhere in the dashboard.
 */
export const CopyAddressButton = ({ address, "data-test-id": testId }: CopyAddressButtonProps) => {
  const intl = useIntl();
  const [copied, copy] = useClipboard();
  const label = intl.formatMessage(messages.copyAddress);
  const iconClassName = sprinkles({ color: "default2" });

  return (
    <Button
      type="button"
      variant="tertiary"
      size="small"
      icon={
        copied ? (
          <Check size={ICON_SIZE} className={iconClassName} />
        ) : (
          <Copy size={ICON_SIZE} className={iconClassName} />
        )
      }
      aria-label={label}
      title={label}
      data-test-id={testId}
      data-copied={copied}
      onClick={() => copy(formatAddressText(address))}
    />
  );
};
